<template>
  <div
    class="ai-mapping-instance"
    :class="{ 'is-disabled': isDisabled }"
    :draggable="dragHandle"
    @dragstart="$emit('drag-element', $event)"
    @drop="$emit('drop-element', $event)"
    @dragover.prevent
    @dragenter.prevent
    @dragleave.prevent
  >
    <div class="ai-variable">
      <div class="ai-value" :class="{ selected: mapping.expression }">
        <p>{{ mapping.value }}</p>
      </div>
    </div>
    <div class="spacer"></div>
    <div class="user-input">
      <div class="user-variable" ref="wrapper">
        <div v-if="!mapping.expression">
          <button class="select-variable" @click="onSelectVariable($event)">
            {{ $t("ai-sections.select-variable") }}
          </button>
        </div>

        <div v-else class="expression-instance">
          <div
            v-if="expressionView"
            @click="onSelectVariable($event)"
            :title="mapping.expression"
            class="advanced-expression ellipsis"
          >
            {{ mapping.expression }}
          </div>
          <div v-else class="variable-expression">
            <div class="variable-name">
              <span @click="onSelectVariable($event)">
                {{ variable.displayName || variablePrettyName }}
              </span>
            </div>
            <div
              class="format-in-variable ellipsis"
              v-if="expressionToVariable.format"
              @click="$emit('on-format-variable', $event, mapping)"
              :style="{ maxWidth: maxWidth }"
            >
              <span>
                {{ expressionToVariable.format }}
              </span>
            </div>
          </div>

          <dropdown left>
            <span slot="toggle" class="format-toggle">
              <div
                class="format-variable-btn"
                :class="{ 'is-advanced': expressionView, opened: expand }"
                @click="expand = !expand"
              >
                <i class="fa-light fa-ellipsis-v-alt"></i>
              </div>
            </span>
            <div class="dropdown-item" v-if="!expressionView" @click="$emit('on-format-variable', $event, mapping)">
              Format +
            </div>
            <div class="dropdown-item" @click="$emit('trash-variable')">
              <span>{{ $t("remove") }} </span> <i class="fa-light fa-trash"></i>
            </div>
          </dropdown>
        </div>
      </div>
    </div>
    <div class="variable-example">
      <p class="text-muted ellipsis" :title="expressionExample">
        {{ expressionExample || $t("ai-sections.no-example") }}
      </p>
    </div>
    <div class="active-status">
      <checkbox class="checkbox" :label="''" v-model="mapping.active" :inactiveColor="'var(--charcoal-115)'"/>
    </div>

    <div class="drag-grip-mapping" @mousedown="dragHandle = true" @mouseleave="dragHandle = false">
      <i class="fa-light fa-grip-dots-vertical"></i>
    </div>

    <div class="delete-mapping" @click="$emit('on-remove-mapping')">
      <i class="fa-light fa-trash"></i>
    </div>
  </div>
</template>

<script>
import VariableHelpers from "@/assets/js/variableHelpers.js";
import Checkbox from "@/components/Checkbox";
import debounce from "lodash/debounce";
const mappingLanguagReference = require("@/assets/js/mappingLanguageReference.json");
export default {
  components: {
    Checkbox
  },
  props: {
    mapping: Object
  },
  data() {
    return {
      hasFormat: false,
      expand: false,
      variableExample: "",
      maxWidth: 100 + "px",
      languageReference: mappingLanguagReference,
      getExpressionValueDebounce: debounce((expression) => {
        this.getExpressionValue(expression);
      }, 500),
      expressionExample: "",
      dragHandle: false
    };
  },
  methods: {
    onSelectVariable(e) {
      this.$emit("on-open-variable-list", e, this.mapping);
    },
    getExpressionValue(expression) {
      this.$store.dispatch("testExpression", expression).then((res) => {
        this.expressionExample = res.result ? res.result : this.$t("ai-sections.no-evaluate");
      });
    }
  },

  computed: {
    expressionToVariable() {
      if (VariableHelpers.stringIsSingleVariable(this.mapping.expression)) {
        return {
          name: VariableHelpers.removeFormatting(this.mapping.expression),
          format: VariableHelpers.getFormatting(this.mapping.expression)
        };
      } else return null;
    },

    variablePrettyName() {
      return this.expressionToVariable ? VariableHelpers.removeBrackets(this.expressionToVariable.name) : "test";
    },

    variable() {
      // return the actual variable, not the instance
      if (this.expressionToVariable) {
        const variable = this.$store.getters.combinedVariables[this.expressionToVariable.name];
        return variable ? variable : { displayName: this.variablePrettyName, value: this.$t("ai-sections.no-value") };
      } else return null;
    },

    isDisabled() {
      return this.mapping.expression && !this.mapping.active;
    },

    mappingDisplayName() {
      const displayName = this.languageReference[this.mapping.value];
      return displayName ? displayName[this.$store.getters.language] : this.mapping.value;
    },

    expressionView() {
      return !VariableHelpers.stringIsSingleVariable(this.mapping.expression);
    }
  },
  watch: {
    "mapping.expression": {
      handler: function() {
        if (this.mapping.expression) this.getExpressionValueDebounce(this.mapping.expression);
        this.$emit("expression-value", this.mapping.expression);
        this.expand = false;
      },
      immediate: true,
      deep: true
    }
  }
};
</script>

<style lang="less">
.ai-mapping-instance {
  position: relative;
  display: grid;
  padding: 7px 12px;
  //   grid-template-columns: repeat(5, 1fr);
  grid-template-columns: minmax(max-content, 240px) 1px minmax(max-content, 300px) 3fr 86px;
  align-items: center;
  &:hover {
    background: #6e757a50;
  }
  .ai-variable {
    max-width: 240px;
    padding-right: 48px;
  }
  .ai-value {
    border-radius: 3px;
    border: 1px solid #fff;
    opacity: 0.5;
    padding: 8px 12px;
    margin-right: 18px;
    display: flex;
    flex-direction: row;
    align-items: center;
    p {
      font-size: 14px;
      font-weight: 700;
      margin: 0;
    }
  }
  .selected {
    opacity: 1;
  }
  .spacer {
    background-color: #fff;
    height: 137%;
  }
  .spacer ~ .spacer {
    height: 50%;
  }
  .user-variable {
    margin-left: 18px;
    white-space: nowrap;
    padding-left: 48px;
    .select-variable {
      width: fit-content;
      background: none;
      border: none;
      color: #fff;
      opacity: 0.5;
      border-radius: 3px;
      border: 1px solid #fff;
      padding: 8px 12px;
      padding-right: 43px;
      display: flex;
      flex-direction: row;
      align-items: center;
      &:hover {
        opacity: 1;
      }
    }
  }

  .variable-example {
    display: flex;
    margin-left: 80px;
    flex-direction: row;
    max-width: 220px;
    align-items: center;
    p {
      margin: 0;
    }
  }
  .active-status {
    width: 100%;
    justify-self: end;
    padding-bottom: 6px;
    margin-right: 15px;
    .container-checkbox .checkbox-check-indicator {
      // background-color: var(--charcoal-115);
    }
    // .container-checkbox input:checked ~ .checkbox-check-indicator {
    //   background-color: var(--orange-web);
    // }
  }

  &.is-disabled {
    .ai-variable {
      opacity: 0.2;
    }
    .variable-expression,
    .advanced-expression,
    .format-in-variable,
    .format-variable-btn {
      opacity: 0.2;
      &:hover {
        opacity: 0.6;
      }
      .dropdown {
        opacity: 1;
      }
      .menu {
        opacity: 1;
      }
    }
    .variable-example {
      opacity: 0.4;
    }
  }

  .drag-grip-mapping {
    cursor: grab;
    position: absolute;
    right: 32px;
    visibility: hidden;

    &:focus {
      cursor: grabbing;
    }
  }

  .delete-mapping {
    cursor: pointer;
    position: absolute;
    right: 6px;
    visibility: hidden;
  }
}

.ai-mapping-instance:hover {
  .drag-grip-mapping,
  .delete-mapping {
    visibility: visible;
  }
}
.user-input {
  max-width: 300px;

  position: relative;
  display: flex;
  align-items: center;
}

.expression-instance {
  z-index: 2;
  display: inline-flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: #fff;
  gap: 2px;
  max-width: 250px;

  .variable-name,
  .format-in-variable,
  .advanced-expression {
    cursor: pointer;
    background: var(--flame);
    // flex: 1 1 auto;
    padding: 8px 12px;
    font-weight: bold;
    border-radius: 3px 0 0 3px;
  }

  .advanced-expression {
    background: unset;
    background: var(--charcoal-border);
    border: 1px solid #fff;
  }

  .variable-expression {
    display: inline-flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    color: #fff;
    gap: 2px;
  }

  .format-in-variable {
    font-weight: normal;
    border-radius: 0;
  }
  .format-toggle {
    width: 13px;
    cursor: pointer;
  }

  .format-variable-btn,
  .add-format-btn,
  .trash-variable-btn {
    border-radius: 0 3px 3px 0;
    background: var(--flame);
    padding: 8px 12px;
    border: none;
    color: #fff;

    &.is-advanced {
      background: unset;
      background: var(--charcoal-border);
      border: 1px solid #fff;
    }
  }

  .add-format-btn,
  .trash-variable-btn {
    border-radius: 0;
    background: #c0583a;
  }
  .trash-variable-btn {
    // border-left: 2px solid var(--charcoal);
    border-radius: 0 3px 3px 0;
  }

  .actions-wrapper {
    position: relative;
    z-index: 0;
  }
  .expand-actions {
    position: absolute;
    display: flex;
    flex-direction: row;
    gap: 2px;
    transform: translateY(-50%);
  }
  // .format-variable-btn.opened {
  //   border-radius: 0px;
  // }
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.1s;
}
.slide-enter-to {
  width: 1px;
  // transform: translateX(0px);
  // opacity: 1;
}
.slide-enter,
.slide-leave-to {
  width: 100%;
  // transform: translateX(-60px);
  // opacity: 0;
}
@media only screen and (max-width: 1450px) {
  .ai-mapping-instance {
    .variable-example {
      margin-left: 30px;
    }
  }
}
@media only screen and (max-width: 1250px) {
  .ai-mapping-instance {
    grid-template-columns: minmax(max-content, 120px) 1px minmax(max-content, 150px) 3fr 86px;
    padding-right: 0;
    .variable-example {
      margin-left: 00px;
      margin-right: 10%;
    }
    .ai-variable {
      width: 110px;
      display: flex;
      justify-content: flex-start;
      padding-right: 0;
      margin: 0;
    }

    .ai-value {
      margin: 0;
      width: 100px;
      padding-right: 0;
    }
    .user-variable {
      padding: 0;
      display: flex;
      justify-content: flex-start;
      align-items: flex-end;
      .select-variable {
        width: 100px;
        font-size: 11px;
        margin-right: 20px;
        padding-left: 8px;
      }
    }
    .variable-example {
      margin-left: 30px;
      max-width: 200px;
    }
  }
  .active-status {
    .container-checkbox .checkbox-check-indicator {
      background-color: var(--charcoal-115);
      // margin-right:120px;
    }
    // .container-checkbox input:checked ~ .checkbox-check-indicator {
    //   background-color: var(--orange-web);
    // }
  }
}
</style>
